import * as React from "react";
import {
  AriaSwitchProps,
  VisuallyHidden,
  useFocusRing,
  useHover,
  usePress,
  useSwitch,
} from "react-aria";
import { useToggleState } from "react-stately";
import { Flex } from "../flex";
import { Color, ColorVariant } from "../../utils/theme";
import { cx } from "../../utils/dom";
import { omit } from "../../utils/object";

interface Props extends AriaSwitchProps {
  color?: Color;
  variant?: ColorVariant;
  size?: "sm" | "md" | "lg";
  className?: string;
}

export const Switch = React.forwardRef<HTMLLabelElement, Props>(
  ({ color = "violet", size = "sm", children, className, ...props }, ref) => {
    const { isHovered, hoverProps } = useHover(props);
    const { isPressed, pressProps } = usePress(props);
    const state = useToggleState(props);
    const switchRef = React.useRef(null);
    const { inputProps } = useSwitch(props, state, switchRef);
    const { isFocusVisible, focusProps } = useFocusRing();

    return (
      <Flex
        ref={ref as any}
        style={{
          opacity: props.isDisabled ? 0.4 : 1,
        }}
        align={"center"}
        gap={"xs"}
        as="label"
        className={cx(className, "p-switch-group")}
      >
        <VisuallyHidden>
          <input ref={switchRef} {...inputProps} {...focusProps} />
        </VisuallyHidden>

        <span
          data-color={color}
          data-hovered={isHovered}
          data-disabled={props.isDisabled}
          data-selected={state.isSelected}
          data-focused={isFocusVisible}
          className="p-switch track"
          data-size={size}
          {...hoverProps}
        >
          <Flex
            data-selected={state.isSelected}
            data-pressed={isPressed}
            className="p-switch-thumb"
            {...omit(pressProps as any, ["isSelected"])}
          />
        </span>
        {children}
      </Flex>
    );
  }
);

Switch.displayName = "Switch";
